<template>
    <wrapper-mobile-bottom-features-wrapper
    animateEntry
    hideBreadcrumbsButShowOnCurtain
    :showWelcomeMessage="!isCompanySession"
    :showSearchOnHeaderMenu="!isCompanySession"
    :applyParticularHomeBackgroundColor="!isCompanySession"
    :applyHomeRenderDelay="applyHomeRenderDelay"
    :customBreadcrumbs="{ currentArea: t('menu.mobileMenu.home') }"
    @applyLoginLoaderExitAnimation="emit('applyLoginLoaderExitAnimation')">
        <!-- Exchanges Company Slider -->
        <exchanges-rates
        v-if="isCompanySession"
        :showExchangesLoader="showExchangesLoader"
        :applyExchangesErrorState="applyExchangesErrorState"/>
        <!-- Header -->
        <home-header
        v-if="isCompanySession"
        :key="currentSelectedAccountGroup"
        :applyPatrimonyErrorState="applyPatrimonyErrorState"
        @hideAmounts="hideAmounts = $event"/>
        <!-- Header Particular-->
        <home-header-particular
        v-else
        :key="`particular${currentSelectedAccountGroup}`"
        :applyAccountsErrorState="applyAccountsErrorState"
        :showAccountsLoader="showAccountsLoader"
        @updateSelectedAccount="selectedAccount = $event"
        @hideAmounts="hideAmounts = $event"/>
        <!-- Fast Links Particular -->
        <widget-fast-links
        v-if="!isCompanySession"
        :headerCards="!isCompanySession"
        :selectedAccount="selectedAccount"/>
        <!-- Content -->
        <div :key="currentSelectedAccountGroup" class="white-layer-wrapper" :class="{'particular-background': !isCompanySession}">
            <operations-pending-button v-if="isCompanySession"/>
            <component
            v-for="(obj, index) in widgetsAnchorComponents"
            variant="anchor"
            :is="obj.component"
            :key="`customWidget${index}`"
            :hideBalance="hideAmounts"
            :selectedAccount="selectedAccount"
            :showPatrimonyLoader="showPatrimonyLoader"
            :showAccountsLoader="showAccountsLoader"
            :applyPatrimonyErrorState="applyPatrimonyErrorState"
            :applyAccountsErrorState="applyAccountsErrorState"
            :hideAmounts="hideAmounts"/>
            <!-- Frequent Operations -->
            <widget-frequent-operations-mobile :whiteBackground="!isCompanySession"/>
        </div>
        <!-- Note: it's called custom, bcuz in a second fase user will be able to choose what he sees here and the order of them -->
        <div :key="currentSelectedAccountGroup" class="custom-widgets-wrapper">
            <div v-if="isCompanySession" class="top-wrapper">
                <div class="title">{{t('home.widgets.applicationsTitle')}}</div>
                <!-- <button-tertiary icon="plus" buttonSize="small" :label="t('home.widgets.applicationsButton')"/> 2º fase -->
            </div>
            <!-- Widgets Wrapper -->
            <div class="bottom-wrapper">
                <component
                v-for="(obj, index) in widgetsComponents.filter(obj => !obj.hideMe)"
                :is="obj.component"
                :key="`customWidget${index}`"
                :hideBalance="hideAmounts"
                :simplifiedVersion="!isCompanySession"
                :showExchangesLoader="showExchangesLoader"
                :showPatrimonyLoader="showPatrimonyLoader"
                :showAccountsLoader="showAccountsLoader"
                :applyExchangesErrorState="applyExchangesErrorState"
                :applyPatrimonyErrorState="applyPatrimonyErrorState"
                :applyAccountsErrorState="applyAccountsErrorState"
                :hideAmounts="hideAmounts"
                @hideMe="hideComponentHandler(obj.id)"/>
            </div>
        </div>
    </wrapper-mobile-bottom-features-wrapper>
    <!-- Remember Me Alert Modal -->
    <modal-centered-generic
    v-if="showAlertRememberMeModal"
    secondModalLayer
    titleIcon="saved-login"
    leftButtonIcon="arrow-right-full"
    titleIconSize="big"
    :title="t('home.alertRememberMeTitle')"
    :text="t('home.alertRememberMeText')"
    :leftButtonText="t('home.leftButtonText')"
    @hideMe="[updateShowAlertRememberMeModal(false), showAlertRememberMeModal = false]"/>
    <!-- Association Alert Modal -->
    <modal-centered-generic
    v-if="showAssociationModal"
    titleIcon="info"
    rightButtonIcon="arrow-right-full"
    :title="t('home.associationModal.title')"
    :text="t('home.associationModal.text')"
    :leftButtonText="t('home.associationModal.leftButtonText')"
    :rightButtonText="t('home.associationModal.rightButtonText')"
    @leftButtonClicked="hideAssociationModalHandler"
    @rightButtonClicked="[hideAssociationModalHandler(), navigateByName(routeList.USER_AREA_DEFINITIONS_SECURE_ASSOCIATION_ENROLLMENT_NAME)]"
    @hideMe="hideAssociationModalHandler"/>
</template>

<script setup>
import WrapperMobileBottomFeaturesWrapper from '@/components/wrappers/featuresTemplates/MobileBottomFeaturesWrapper'
import ExchangesRates from '@/components/features/home/ExchangesRates.vue'
import HomeHeader from '@/components/features/home/Header'
import HomeHeaderParticular from '@/components/features/home/HeaderParticularMobile'
import OperationsPendingButton from '@/components/features/home/OperationsPendingButton'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
// import ButtonTertiary from '@/components/generic/buttons/Tertiary' 2º fase component
/* Widgets */
import WidgetCard from '@/components/features/home/widgets/Cards'
import WidgetNaHora from '@/components/features/home/widgets/NaHora'
import WidgetCashflowPatrimony from '@/components/features/home/widgets/CashflowPatrimony'
import WidgetFrequentOperationsMobile from '@/components/features/home/widgets/frequentOperations/FrequentOperations'
import WidgetBalancesAndMovements from '@/components/features/home/widgets/BalancesAndMovements'
import WidgetBalancesAndMovementsParticular from '@/components/features/home/widgets/BalancesAndMovementsParticular'
import WidgetFastLinks from '@/components/features/home/widgets/FastLinks'
import WidgetExchanges from '@/components/features/home/widgets/Exchanges'
import WidgetTpas from '@/components/features/home/widgets/TPAs'
import WidgetMerchant from '@/components/features/home/widgets/Merchant'
// import WidgetCampaigns from '@/components/features/home/widgets/Campaigns' 2º fase
// import WidgetAdiantaJa from '@/components/features/home/widgets/AdiantaJa' 2º fase

/* Vue & Helpers */
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { useRoute } from 'vue-router'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['applyLoginLoaderExitAnimation'])

/* Props */
const props = defineProps({
    applyHomeRenderDelay: Boolean
})

/* mapActions & mapGetters */
const { requestAccountsList, fetchHomePatrimony, fetchMerchantStatus, requestExchangeTableList, clearFeatureStates } = mapActions(moduleMapper.HOME)
const { updateShowAlertRememberMeModal } = mapActions()
const { updateEntityRememberInfo } = mapActions(moduleMapper.SESSION)
const { getAccountGroups, getRememberMeEntityInfo, getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getShowAlertRememberMeModalState } = mapGetters()
const { getMenu } = mapGetters(moduleMapper.MENU)

const currentRoute = useRoute()

const showAlertRememberMeModal = ref(false)

const isCompanySession = computed(() => {
    return getAccountGroups.value.find(obj => obj.default).type.toLowerCase() === 'company'
})

const currentSelectedAccountGroup = computed(() => {
    return getAccountGroups.value.find(obj => obj.default)
})

const hideAmounts = ref(false)
const selectedAccount = ref(null)

const selectedAcession = computed(() => {
    if (!getRememberMeEntityInfo.value) return null
	return getRememberMeEntityInfo.value.listOfAcessions.find(el => el.acessionNumber === getAllCustomerInfo.value.acessionNumber)
})

const showAssociationModal = computed(() => {
    if (!selectedAcession.value) return null
    return selectedAcession.value.hasBiometricEnrolled && selectedAcession.value.hadSecuredAssociation && !getAllCustomerInfo.value.alreadyHasActiveSecureAssociation && currentRoute.path === routeList.HOME_MOBILE_PATH
})

const hideAssociationModalHandler = () => {
    const entityParsed = {
        ...getRememberMeEntityInfo.value,
        listOfAcessions: getRememberMeEntityInfo.value.listOfAcessions.map(accession => {
            return {
                ...accession,
                hadSecuredAssociation: false
            }
        })
    }

    updateEntityRememberInfo(entityParsed)
}
/*************************************************
*                                                *
*                 Widgets Mappers                *
*                                                *
*************************************************/
const particularAnchorComponents = ref([
    { component: WidgetBalancesAndMovementsParticular, type: 'anchor' }
])

const particularComponents = ref([
    { component: WidgetCashflowPatrimony }
])

const companyAnchorComponents = ref([
	{ component: WidgetCashflowPatrimony, type: 'anchor' },
	{ component: WidgetFastLinks, type: 'anchor' }
])

const companyComponents = ref([
	{ component: WidgetBalancesAndMovements },
	{ component: WidgetNaHora },
	{ component: WidgetTpas },
	{ component: WidgetExchanges },
    { component: WidgetCard, id: 'cardWidget' },
	{ component: WidgetMerchant, id: 'merchantWidget', hideMe: true }
])

const widgetsAnchorComponents = computed(() => {
    return isCompanySession.value ? companyAnchorComponents.value : particularAnchorComponents.value
})

const widgetsComponents = computed(() => {
    return isCompanySession.value ? companyComponents.value : particularComponents.value
})

const hideComponentHandler = widgetId => {
    if (isCompanySession.value) {
        companyComponents.value = companyComponents.value.map(obj => {
            return obj.id && obj.id === widgetId ? { ...obj, hideMe: true } : obj
        })
    }
}

/*************************************************
*                                                *
*                Accounts Request                *
*                                                *
*************************************************/
const showAccountsLoader = ref(false)
const applyAccountsErrorState = ref(false)

const requestAccounts = async () => {
    showAccountsLoader.value = true

    try {
        await requestAccountsList()
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyAccountsErrorState.value = true
    } finally {
        showAccountsLoader.value = false
    }
}

/*************************************************
*                                                *
*                Patrimony Request               *
*                                                *
*************************************************/
const showPatrimonyLoader = ref(false)
const applyPatrimonyErrorState = ref(false)

const requestPatrimonyInfo = async () => {
    showPatrimonyLoader.value = true

    try {
        await fetchHomePatrimony()
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyPatrimonyErrorState.value = true
	} finally {
        showPatrimonyLoader.value = false
    }
}

/*************************************************
*                                                *
*              Exchanges Request           *
*                                                *
*************************************************/
const showExchangesLoader = ref(false)
const applyExchangesErrorState = ref(false)

const requestExchanges = async () => {
    showPatrimonyLoader.value = true

    const payload = { currency: 'AKZ', quotationDate: new Date() }

    try {
        await requestExchangeTableList(payload)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyExchangesErrorState.value = true
	} finally {
        showExchangesLoader.value = false
    }
}

/*************************************************
*                                                *
*              Merchant Status Request           *
*                                                *
*************************************************/
const requestMerchantStatus = async () => {
    try {
        const res = await fetchMerchantStatus()
        if (!res.data.data.hasMerchant) {
            companyComponents.value = companyComponents.value.map(obj => {
                return obj.id === 'merchantWidget' ? { ...obj, hideMe: false } : obj
            })
        }
	} catch {
		/* Do nothing */
	}
}

/*
* We are doing these requests here bcuz this info will feed multiple widgets
* or control if some widgets are shown or not
*/
const commonWidgetsFetches = () => {
    requestAccounts()
    requestPatrimonyInfo()
    requestExchanges()
    setTimeout(() => {
        if (isCompanySession.value &&
            !getMenu.value.find(obj => obj.link === '/approvals' && !!obj.children.find(child => child.link === '/approvals/operators'))) {
            hideComponentHandler('operatorsWidget')
        }
    })
    if (isCompanySession.value) requestMerchantStatus()
    if (getShowAlertRememberMeModalState.value) showAlertRememberMeModal.value = true
}

watch(() => currentSelectedAccountGroup.value, (newVal, oldVal) => {
    commonWidgetsFetches()
})

commonWidgetsFetches()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.white-layer-wrapper {
    width: 100%;
    padding: 0 16px 16px 16px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    &:not(.particular-background) {
        background-color: var(--secondary-light-4);
    }

    &.particular-background {
        position: relative;

        &:before {
            content: '';
            width: 100%;
            height: calc(100% - 24px);

            position: absolute;
            top: 40px;
            left: 0;

            background-color: var(--secondary-light-3);
            border-radius: 16px 16px 0 0;
        }
    }
}

.custom-widgets-wrapper {
    width: 100%;
    padding: 16px;

    display: flex;
    flex-direction: column;
    gap: 16px;

    background-color: var(--secondary-light-3);
    border-radius: 16px;

    .top-wrapper {
        width: 100%;

        display: flex;
        justify-content: space-between;
        align-items: center;

        .title {
            @include H5-Medium;
            color: var(--secondary-dark-3);
        }
    }

    .bottom-wrapper {
        width: 100%;

        display: flex;
        flex-direction: column;
        gap: 16px;
    }
}
</style>
